import { HttpClient } from '../api/httpclient.service';

export interface LessonProgressPayload {
  lessonId: number;
  status: 0 | 1 | 2;
  percentage: number;
  lastInteractionId: string | null;
  completedInteractionIds: string[];
  totalInteractions: number;
  interactionStates?: Record<string, any>;
}

export interface LessonProgressResponse {
  lesson_id: number;
  status: number;
  percentage: number;
  full_topic_label?: string;
  level_name?: string;
  completed_count?: number;
  total_count?: number;
  last_interaction_id?: string | null;
  completed_interaction_ids?: string[];
  total_interactions?: number;
  started_at?: string;
  last_updated_at?: string;
  completed_at?: string;
  interaction_states?: Record<string, any>;

  // Metadata (may be returned by single getProgress if joined)
  topic_id?: number;
  level_id?: number;
  sub_topic_id?: number;
}

export class LessonProgressService extends HttpClient {
  constructor() {
    super();
  }

  public async upsertProgress(payload: LessonProgressPayload): Promise<void> {
    try {
      const backendPayload = {
        lesson_id: payload.lessonId,
        status: payload.status,
        percentage: payload.percentage,
        last_interaction_id: payload.lastInteractionId,
        completed_interaction_ids: payload.completedInteractionIds,
        total_interactions: payload.totalInteractions,
        interaction_states: payload.interactionStates,
      };
      await this.post('/lesson-progress/upsert', backendPayload);
    } catch (error) {
      console.error('Error syncing lesson progress:', error);
      throw error;
    }
  }

  private static _activePromises = new Map<string, Promise<any>>();

  private async _deduplicate<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    if (LessonProgressService._activePromises.has(key)) {
      return LessonProgressService._activePromises.get(key);
    }
    const promise = fetcher().finally(() => {
      LessonProgressService._activePromises.delete(key);
    });
    LessonProgressService._activePromises.set(key, promise);
    return promise;
  }

  public async getProgress(lessonId: number): Promise<LessonProgressResponse | null> {
    return this._deduplicate(`progress:${lessonId}`, () => 
      this.get<LessonProgressResponse>(`/lesson-progress/${lessonId}`).catch(error => {
        if (error.response && error.response.status === 404) return null;
        throw error;
      })
    );
  }

  public async getAllProgress(): Promise<LessonProgressResponse[]> {
    return this._deduplicate('progress:all', () => 
      this.get<LessonProgressResponse[]>('/lesson-progress/all')
    );
  }
}
